import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { listarOrdenes, filtrarOrdenes, limpiarOrdenes } from '../../../actions/ordenesSalida';

import EliminarOrdenModal from './modals/EliminarOrdenModal';

const opcionesStatus = [
    { value: 0, label: 'Creada' },
    { value: 1, label: 'Semi-entregada' },
    { value: 2, label: 'Entregada' },
    { value: 3, label: 'Cancelada' },
    { value: 4, label: 'Eliminada' }
];

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const renderBadgeStatus = (status) => {
    const s = Number(status);
    if (s === 0) return <span className="badge bg-info text-dark">Creada</span>;
    if (s === 1) return <span className="badge text-dark" style={{ backgroundColor: '#fd7e14' }}>Semi-entregada</span>;
    if (s === 2) return <span className="badge bg-success">Entregada</span>;
    if (s === 3) return <span className="badge bg-warning text-dark">Cancelada</span>;
    if (s === 4) return <span className="badge bg-secondary">Eliminada</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

const OrdenesSalidaProyecto = ({ idProyecto }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { filtrado } = useSelector(state => state.ordenesSalida);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [statusSel, setStatusSel] = useState(null);
    const [ordenEliminar, setOrdenEliminar] = useState(null);

    useEffect(() => {
        if (!idProyecto) return;

        dispatch(listarOrdenes({ id_proyecto: idProyecto }));

        return () => {
            dispatch(limpiarOrdenes());
        };
    }, [dispatch, idProyecto]);

    const onChangeFiltroTabla = (e) => {
        const val = e.target.value;
        setTextoFiltro(val);
        dispatch(filtrarOrdenes(val));
    };

    const onChangeStatus = (opcion) => {
        setStatusSel(opcion);
        dispatch(listarOrdenes({ id_proyecto: idProyecto, status: opcion?.value ?? null }));
    };

    const columns = useMemo(() => ([
        {
            name: 'Folio',
            selector: row => row?.folio || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Cotización de origen',
            selector: row => row?.cotizacion?.folio || '',
            sortable: true,
            wrap: true,
            grow: 1,
            cell: row => (
                row?.cotizacion?._id
                    ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            title="Ver cotización de origen"
                            onClick={() => navigate(`/proyectos/cotizaciones/detalle/${row.cotizacion._id}`)}
                        >
                            {row.cotizacion.folio}
                        </button>
                    )
                    : 'No aplica'
            )
        },
        {
            name: 'Fecha',
            selector: row => formatDate(row?.fecha),
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Total',
            selector: row => row?.total || 0,
            sortable: true,
            right: true,
            cell: row => (
                <div style={{ width: '100%', textAlign: 'right' }}>
                    {formatMoney(row?.total)}
                </div>
            ),
            grow: 1
        },
        {
            name: 'Status',
            sortable: true,
            width: '140px',
            cell: row => renderBadgeStatus(row?.status)
        },
        {
            name: 'Opciones',
            width: '180px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Ver detalle"
                        onClick={() => navigate(`/proyectos/ordenes/detalle/${row._id}`)}
                    >
                        <i className="fas fa-eye text-primary" />
                    </button>

                    {[0, 1, 2].includes(Number(row?.status)) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title={Number(row?.status) === 0 ? 'Editar' : 'Editar precios'}
                            onClick={() => navigate(`/proyectos/ordenes/editar/${row._id}`)}
                        >
                            <i className="fa fa-edit text-warning" />
                        </button>
                    )}

                    {[0, 3].includes(Number(row?.status)) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Eliminar"
                            onClick={() => setOrdenEliminar(row)}
                        >
                            <i className="fas fa-trash text-danger" />
                        </button>
                    )}
                </div>
            )
        }
    ]), [navigate]);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                    <div style={{ minWidth: 280 }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar en la tabla..."
                            value={textoFiltro}
                            onChange={onChangeFiltroTabla}
                        />
                    </div>

                    <div style={{ minWidth: 200 }}>
                        <Select
                            options={opcionesStatus}
                            value={statusSel}
                            onChange={onChangeStatus}
                            isClearable
                            placeholder="Status..."
                            noOptionsMessage={() => 'Sin resultados'}
                        />
                    </div>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => navigate(`/proyectos/ordenes/nueva/${idProyecto}`)}
                    >
                        <i className="feather icon-plus" /> Agregar orden
                    </button>
                </div>
            </div>

            <div className="mt-2">
                <DataTable
                    columns={columns}
                    data={filtrado || []}
                    pagination
                    paginationComponentOptions={paginationOptions}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    noDataComponent="Sin órdenes de salida para mostrar"
                />
            </div>

            <EliminarOrdenModal
                show={!!ordenEliminar}
                idProyecto={idProyecto}
                idOrden={ordenEliminar?._id || null}
                onHide={() => setOrdenEliminar(null)}
            />
        </Fragment>
    );
};

export default OrdenesSalidaProyecto;
